import { DataService } from "@/services/data";
import { DynamicAction } from "@/types/dynamic-detail";
import { toast } from "sonner";
import type { FilterTree } from "./filter-tree/types";
import type { ActionModalProps } from "./types2";

type PrepareScope = {
    endpointUrl: string;
    action_scope: "list" | "detail";
    ids: (string | number)[];
    is_all?: boolean;
    drqQuery?: string | null;
    /** Advanced filters of the list. Sent with select-all so the preview sees those rows. */
    filterTree?: FilterTree | null;
};

/**
 * The dialog of an action as the server prepares it (`prepare_before_open`: its fields, defaults and context), then
 * `values` over its defaults: what a refusal carried over (`ActionResolution`). `null` when the server refuses it
 * (the reason is toasted).
 */
export async function prepareAction(action: DynamicAction, scope: PrepareScope, values?: Record<string, unknown>): Promise<ActionModalProps | null> {
    let prepared: ActionModalProps = {
        action_title: action.title,
        action_description: action.description,
        action_api_name: action.action_name,
        redirect_url: action.redirect_url,
        redirect_target: action.redirect_target,
        button_label: action.button_label,
        fields: action.fields,
        checks: action.checks,
        sync: action.sync
    };

    if (action.prepare_before_open) {
        try {
            const filterTree =
                scope.is_all && scope.filterTree && scope.filterTree.root.children.length > 0 ? scope.filterTree : null;
            const request = DataService.client.url(scope.endpointUrl + "actions/").query({
                action_api_name: action.action_name,
                action_scope: scope.action_scope,
                ids: scope.ids,
                is_all: scope.is_all || undefined,
                ...(scope.is_all && scope.drqQuery ? { query: scope.drqQuery } : {})
            });
            // The tree rides in the body (`precheck` does not run the action): on the query
            // string it is repeated by the CORS preflight and gunicorn refuses the line.
            const response = await (filterTree ? request.post({ precheck: true, filter_tree: filterTree }) : request.get()).json<{
                title: string;
                description: string;
                helper_text?: string | null;
                fields: DynamicAction["fields"];
                defaults?: Record<string, unknown>;
                eligibility: { status: "ok" | "warning" | "error"; message?: string | null };
                context?: string | null;
            }>();
            if (response.eligibility.status === "error") {
                toast.error(response.eligibility.message || "This action is not available.");
                return null;
            }
            if (response.eligibility.status === "warning" && response.eligibility.message) {
                toast.warning(response.eligibility.message);
            }
            prepared = {
                ...prepared,
                action_title: response.title,
                action_description: [response.description, response.helper_text].filter(Boolean).join("\n\n"),
                fields: response.fields.map(field => ({
                    ...field,
                    value: response.defaults?.[field.field_name] ?? field.value
                })),
                checks: undefined,
                preflight_context: response.context
            };
        } catch (error: unknown) {
            let message = "Unable to prepare this action. Refresh the record and try again.";
            try {
                const parsed = JSON.parse(error instanceof Error ? error.message : "{}") as { error?: string };
                message = parsed.error || message;
            } catch {
                // Keep the actionable fallback.
            }
            toast.error(message);
            return null;
        }
    }

    if (values) {
        prepared = {
            ...prepared,
            fields: prepared.fields.map(field => (field.field_name in values ? { ...field, value: values[field.field_name] } : field))
        };
    }
    return prepared;
}
